import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Steps, message } from 'antd';
import { MailOutlined, SafetyOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Title, Text } = Typography;

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // 发送验证码
  const sendVerificationCode = async (emailValue: string) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/forgot-password/send-code', {
        email: emailValue
      });
      
      message.success(response.data.message || '验证码已发送到您的邮箱');
      setEmail(emailValue);
      setStep(1);
      setCountdown(60); // 开始倒计时
      
      // 倒计时
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      if (error.response?.status === 429) {
        message.warning(error.response.data.message || '请稍后再试');
      } else {
        message.error(error.response?.data?.message || '发送验证码失败，请检查邮件配置');
      }
    } finally {
      setLoading(false);
    }
  };

  // 提交验证码并重置密码
  const onFinish = async (values: { code: string; password: string; confirmPassword: string }) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/forgot-password/reset', {
        email,
        code: values.code,
        newPassword: values.password,
        confirmPassword: values.confirmPassword
      });
      
      message.success(response.data.message || '密码重置成功');
      
      // 成功后跳转到登录页
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码重置失败');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        // 第一步：输入邮箱
        return (
          <Form layout="vertical" onFinish={(values) => sendVerificationCode(values.email)}>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: '请输入邮箱地址' },
                { type: 'email', message: '邮箱格式不正确' }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="请输入您的注册邮箱"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full h-12 rounded-lg font-medium"
                size="large"
              >
                发送验证码
              </Button>
            </Form.Item>
          </Form>
        );

      case 1:
        // 第二步：输入验证码和新密码
        return (
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="code"
              label="验证码"
              rules={[
                { required: true, message: '请输入验证码' },
                { len: 6, message: '验证码为6位数字' },
                { pattern: /^\d{6}$/, message: '验证码必须为6位数字' }
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="请输入6位验证码"
                size="large"
                className="rounded-lg"
                maxLength={6}
              />
            </Form.Item>

            {countdown > 0 && (
              <div className="mb-4 text-center">
                <Text type="secondary">
                  {countdown}秒后可重新发送
                </Text>
              </div>
            )}

            <Form.Item
              name="password"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码长度至少6位' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入新密码"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  }
                })
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请再次输入新密码"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full h-12 rounded-lg font-medium"
                size="large"
              >
                重置密码
              </Button>
            </Form.Item>

            <div className="text-center">
              <Button
                type="link"
                onClick={() => setStep(0)}
                disabled={countdown > 0}
              >
                {countdown > 0 ? `重新发送 (${countdown}s)` : '重新发送验证码'}
              </Button>
            </div>
          </Form>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <Card className="w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-white font-bold text-3xl">?</span>
          </div>
          
          <Title level={2} className="mb-2 text-center">
            找回密码
          </Title>
          <Text type="secondary" className="text-center block">
            通过邮箱验证码重置密码
          </Text>
        </div>

        <Steps
          current={step}
          size="small"
          items={[
            {
              title: '输入邮箱',
              icon: <MailOutlined />
            },
            {
              title: '重置密码',
              icon: <LockOutlined />
            }
          ]}
          className="mb-8"
        />

        {renderStepContent()}

        <div className="text-center mt-6">
          <Text type="secondary">想起密码了？</Text>
          <Link to="/login" className="ml-2 text-blue-500 hover:text-blue-600">
            返回登录
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;
